// Connect Instance Systems Helper — wires the **Systems Quadrad** (four equal peers).
//
// This reduces duplicated boilerplate when creating per-instance systems
// (workspace, dev chat, commit message, etc.). BubbleUiSystem is usually a shared
// runtime instance (same importance as the other three; not "just" a helper).

/**
 * Connect the 4 instance systems for a single UI/workspace runtime.
 *
 * Responsibilities:
 * - Wire quadrad references: ToolSystem <-> AIToolSystem <-> AIInferenceSystem <-> BubbleUiSystem
 * - Provide manager to:
 *   - toolSystem persistence/UI hooks (if toolSystem supports it)
 *   - bubbleUiSystem DOM context (so bubble-authored HTML can call back into UI)
 *
 * @param {Object} params
 * @param {import("../base/ToolSystem.js").ToolSystem} params.toolSystem
 * @param {import("../base/AIToolSystem.js").AIToolSystem} params.aiToolSystem
 * @param {import("../base/AIInferenceSystem.js").AIInferenceSystem} params.aiInferenceSystem
 * @param {import("../ui/BubbleUiSystem.js").BubbleUiSystem} params.bubbleUiSystem
 * @param {*} params.manager - optional section/instance manager instance
 */
export function connectInstanceSystems({
  toolSystem,
  aiToolSystem,
  aiInferenceSystem,
  bubbleUiSystem,
  manager,
}) {
  // Intentionally tolerant: you may provide only a subset of the four systems (+ optional manager).
  // When a referenced dependency is missing, we do NOT set cross references
  // (prevents partially-wired systems from ending up in a broken state).

  // ToolSystem <-> AIToolSystem <-> AIInferenceSystem
  if (toolSystem && aiToolSystem && typeof toolSystem.setAIToolSystem === "function") {
    toolSystem.setAIToolSystem(aiToolSystem);
  }

  if (
    toolSystem &&
    aiInferenceSystem &&
    typeof toolSystem.setAIInferenceSystem === "function"
  ) {
    toolSystem.setAIInferenceSystem(aiInferenceSystem);
  }

  if (
    toolSystem &&
    bubbleUiSystem &&
    typeof toolSystem.setBubbleUiSystem === "function"
  ) {
    toolSystem.setBubbleUiSystem(bubbleUiSystem);
  }

  if (aiToolSystem && toolSystem && typeof aiToolSystem.setToolSystem === "function") {
    aiToolSystem.setToolSystem(toolSystem);
  }

  if (
    aiToolSystem &&
    aiInferenceSystem &&
    typeof aiToolSystem.setAIInferenceSystem === "function"
  ) {
    aiToolSystem.setAIInferenceSystem(aiInferenceSystem);
  }

  if (
    aiToolSystem &&
    bubbleUiSystem &&
    typeof aiToolSystem.setBubbleUiSystem === "function"
  ) {
    aiToolSystem.setBubbleUiSystem(bubbleUiSystem);
  }

  if (aiInferenceSystem && toolSystem && typeof aiInferenceSystem.setToolSystem === "function") {
    aiInferenceSystem.setToolSystem(toolSystem);
  }

  if (
    aiInferenceSystem &&
    aiToolSystem &&
    typeof aiInferenceSystem.setAIToolSystem === "function"
  ) {
    aiInferenceSystem.setAIToolSystem(aiToolSystem);
  }

  if (
    aiInferenceSystem &&
    bubbleUiSystem &&
    typeof aiInferenceSystem.setBubbleUiSystem === "function"
  ) {
    aiInferenceSystem.setBubbleUiSystem(bubbleUiSystem);
  }

  // Manager injection (only when supported by the receiving instance).
  if (manager != null && toolSystem && typeof toolSystem.setWorkspaceManager === "function") {
    toolSystem.setWorkspaceManager(manager);
  }

  if (
    manager != null &&
    aiInferenceSystem &&
    typeof aiInferenceSystem.setWorkspaceManager === "function"
  ) {
    aiInferenceSystem.setWorkspaceManager(manager);
  }

  // Bubble-authored HTML relies on bubble root DOM context callbacks.
  if (bubbleUiSystem && typeof bubbleUiSystem.setSystemContext === "function") {
    const nextContext = {};

    // BubbleUiSystem's DOM context uses the key name `workspaceManager`.
    // Even if the caller uses a different instance type, this is the
    // convention BubbleUiSystem expects.
    if (manager != null) {
      nextContext.workspaceManager = manager;
    }
    if (toolSystem != null) {
      nextContext.toolSystem = toolSystem;
    }
    if (aiToolSystem != null) {
      nextContext.aiToolSystem = aiToolSystem;
    }
    if (aiInferenceSystem != null) {
      nextContext.aiInferenceSystem = aiInferenceSystem;
    }

    bubbleUiSystem.setSystemContext(nextContext);
  }
}

